import React, { useState, useEffect, useRef } from "react";
import { api } from "./api";
import FormSection from "./FormSection";
import FormField from "./FormField";
import FooterLogo from "./FooterLogo";
import {
  patientFields_followup,
  anaestheticFields,
  mandatoryFields_followup,
} from "./formConfig";

export default function FormPage() {
  const initialState = {};
  [
    ...patientFields_followup,
  ].forEach((f) => {
    initialState[f.name] = f.type === "checkbox" ? false : "";
  });
  initialState.non_sa_main_id = false;
  initialState.non_sa_patient_id = false;

  anaestheticFields.forEach(({ name, detailField }) => {
    initialState[name] = "No"; // default to No
    initialState[detailField.name] = "";
  });

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", answer: null });
  const [captchaInput, setCaptchaInput] = useState("");
  const submitButtonRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const a = Math.floor(Math.random() * 10);
    const b = Math.floor(Math.random() * 10);
    setCaptcha({ question: `What is ${a} + ${b}?`, answer: a + b });
  }, []);




  useEffect(() => {
    const extractDOB = (id) => {
      if (!/^\d{6}/.test(id)) return null;
      const yy = id.slice(0, 2);
      const mm = id.slice(2, 4);
      const dd = id.slice(4, 6);
      const year = parseInt(yy, 10) < 30 ? `20${yy}` : `19${yy}`;
      return `${dd}/${mm}/${year}`;
    };

    const extractGender = (id) => {
      if (!/^\d{7}/.test(id)) return null;
      const digit = parseInt(id[6], 10);
      return digit < 5 ? "Female" : "Male";
    };

    const mainDOB = !formData.non_sa_main_id
      ? extractDOB(formData.main_identification_number)
      : null;
    const patientDOB = !formData.non_sa_patient_id
      ? extractDOB(formData.patient_identification_number)
      : null;
    const mainGender = !formData.non_sa_main_id
      ? extractGender(formData.main_identification_number)
      : null;
    const patientGender = !formData.non_sa_patient_id
      ? extractGender(formData.patient_identification_number)
      : null;

    setFormData((prev) => ({
      ...prev,
      ...(mainDOB ? { main_date_of_birth: mainDOB } : {}),
      ...(mainGender ? { main_gender: mainGender } : {}),
      ...(patientDOB ? { patient_date_of_birth: patientDOB } : {}),
      ...(patientGender ? { patient_gender: patientGender } : {}),
    }));
  }, [
    formData.main_identification_number,
    formData.patient_identification_number,
    formData.non_sa_main_id,
    formData.non_sa_patient_id,
  ]);

  useEffect(() => {
    if (formData.patient_date_of_birth) {
      const [dd, mm, yyyy] = formData.patient_date_of_birth.split("/");
      const birthDate = new Date(`${yyyy}-${mm}-${dd}`);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setFormData((prev) => ({ ...prev, patient_age: age }));
    }
  }, [formData.patient_date_of_birth]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let isValid = true;
    const formErrors = {};

    for (let field of mandatoryFields_followup) {
      if (!formData[field]) {
        formErrors[field] = "This field is required.";
        isValid = false;
      }
    }


    if (parseInt(captchaInput) !== captcha.answer) {
      formErrors.captcha = "Incorrect answer.";
      isValid = false;
    }

    if (!isValid) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      );
      if (errorElement?.scrollIntoView) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
      return;
    }

    anaestheticFields.forEach(({ name }) => {
      formData[name] = formData[name] === "Yes" ? 1 : 0;
    });

    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const [dd, mm, yyyy] = dateStr.split("/");
      return new Date(`${yyyy}-${mm}-${dd}`).toISOString();
    };

    const updatedFormData = {
      ...formData,
      main_date_of_birth: parseDate(formData.main_date_of_birth),
      patient_date_of_birth: parseDate(formData.patient_date_of_birth),
      main_male: formData.main_gender === "Male",
      main_female: formData.main_gender === "Female",
      patient_male: formData.patient_gender === "Male",
      patient_female: formData.patient_gender === "Female",
      follow_up: true,
    };

    try {
      await api.post(`/save-patient`, updatedFormData);
      setFormData(initialState);
      setCaptchaInput("");
      setSubmitted(true);
    } catch (err) { 
      if (!err.response || err.code === "ERR_BAD_RESPONSE" || err.status === 504 || err.message.includes("504")) {
        alert("Server is unreachable. Please try again later.");
      } else {
        alert("Failed to Submit Details. Please check your input.");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 py-10">
      {submitted ? (
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center w-full sm:w-96">
          <h1 className="text-2xl font-bold text-green-600 mb-4">Success</h1>
          <p className="text-lg text-gray-700">
            Your information was successfully submitted, thanks.
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-white p-8 rounded-2xl shadow-lg w-full sm:w-96"
        >
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Logo" className="w-81 h-auto" />
          </div>

          <p className="text-sm text-gray-600 text-center mb-6">
            To help streamline your consultation and reduce paperwork on the
            day, please complete the form below in advance. If you
            prefer not to complete them online, you're welcome to do so at the
            consulting rooms—just arrive 15 minutes early.
          </p>

          <FormSection
            title="Patient Information"
            fields={patientFields_followup}
            formData={formData}
            errors={errors}
            onChange={handleChange}

          />

          <FormSection
            title="Patient Anaesthetics Questionnaire"
            fields={anaestheticFields}
            formData={formData}
            errors={errors}
            onChange={handleChange}
            sectionDescription="Has the Patient had the following;"
            insertAfterField={anaestheticFields.reduce((acc, field) => {
              acc[field.name] = formData[field.name] === "Yes" && (
                <FormField
                  key={field.detailField.name}
                  field={field.detailField}
                  value={formData[field.detailField.name]}
                  error={errors[field.detailField.name]}
                  onChange={handleChange}
                />
              );
              return acc;
            }, {})}
          />

          <hr className="my-6 border-gray-600" />

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">
              {captcha.question}
            </label>
            <input
              type="number"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <button
            type="submit"
            ref={submitButtonRef}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl w-full hover:bg-blue-700 transition duration-300 ease-in-out"
          >
            Submit Details
          </button>
        </form>
      )}
      <FooterLogo />
    </div>
  );
}
